import React, { useEffect, useState, useRef } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronDown } from 'lucide-react';
export function Layout() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [scrollDirection, setScrollDirection] = useState<'up' | 'down'>('up');
  const lastScrollY = useRef(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openSection, setOpenSection] = useState<string | null>(null);
  const location = useLocation();
  useEffect(() => {
    setMobileMenuOpen(false);
    setOpenSection(null);
  }, [location.pathname]);
  const toggleSection = (section: string) => setOpenSection(prev => prev === section ? null : section);
  // These routes open with a dark (#1C1510) hero, so while the header is still
  // transparent (before the user scrolls) the nav must switch to a light
  // treatment — white links and a white logo — to stay legible. Once scrolled,
  // the header turns solid white and reverts to the dark-on-light treatment.
  const darkHeroRoutes = ['/compare/chatgpt', '/security', '/demo'];
  const onDark = darkHeroRoutes.includes(location.pathname) && !isScrolled;
  // While at the very top of the homepage the hero already owns the primary
  // "Try Giga for free" CTA, so the header shows a quieter white "Sign in"
  // button until the user scrolls past it.
  const atHeroTop = location.pathname === '/' && !isScrolled;
  const navLinkClass = `text-sm font-medium transition-colors ${onDark ? 'text-white/80 hover:text-white' : 'text-neutral-600 hover:text-[#2B211A]'}`;
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const scrolled = currentScrollY > 20;
      setIsScrolled(scrolled);
      if (scrolled) {
        if (currentScrollY > lastScrollY.current + 5) {
          setScrollDirection('down');
          setMobileMenuOpen(false);
        } else if (currentScrollY < lastScrollY.current - 5) {
          setScrollDirection('up');
        }
      } else {
        setScrollDirection('up');
      }
      lastScrollY.current = currentScrollY;
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  return <div className="min-h-screen bg-[#FAF8F4] text-[#2B211A] font-body selection:bg-[#EA580C]/20 selection:text-[#2B211A]" data-id="element-1208">
      <header className={`fixed top-0 w-full z-50 transition-all duration-300 ${isScrolled ? 'bg-white/90 backdrop-blur-md border-b border-neutral-200 shadow-sm py-3' : 'bg-transparent py-5'} ${isScrolled && scrollDirection === 'down' ? '-translate-y-full' : 'translate-y-0'}`} data-id="element-1209">
        <div className="container mx-auto px-6 flex items-center justify-between" data-id="element-1210">
          <Link to="/" aria-label="Giga home" className="flex items-center gap-2 group" data-id="element-1211">
            <img src="https://cdn.magicpatterns.com/uploads/bxQBDjkPJfWx4r7DHyApcd/Vector.png" alt="" aria-hidden="true" className={`h-7 w-7 object-contain group-hover:scale-105 transition-transform ${onDark ? '[filter:brightness(0)_invert(1)]' : ''}`} data-id="element-1212" />
            <img src="https://cdn.magicpatterns.com/uploads/fUuKba8qU1ywiSyZ4Pf9Xu/giga4x.png" alt="Giga" className={`h-6 object-contain transition-[filter] ${onDark ? '[filter:brightness(0)_invert(1)]' : ''}`} data-id="element-1213" />
          </Link>

          <nav className="hidden md:flex items-center gap-8" data-id="element-1214">
            <Link to="/pricing" className={navLinkClass} data-id="element-1215">
              Pricing
            </Link>
            <div className="relative group" data-id="element-1216">
              <button className={`flex items-center gap-1 ${navLinkClass}`} data-id="element-1217">
                Use Cases{' '}
                <ChevronDown size={14} className="opacity-50 group-hover:rotate-180 transition-transform" data-id="element-1218" />
              </button>
              <div className="absolute top-full left-0 pt-4 opacity-0 translate-y-2 pointer-events-none group-hover:opacity-100 group-hover:translate-y-0 group-hover:pointer-events-auto transition-all duration-200" data-id="element-1219">
                <div className="bg-white border border-neutral-200 shadow-xl rounded-xl py-2 w-56 flex flex-col" data-id="element-1220">
                  <Link to="/use-cases/founders-execs" className="px-4 py-2 text-sm font-medium text-neutral-600 hover:text-[#2B211A] hover:bg-neutral-50 transition-colors" data-id="element-1221">
                    Founders & Execs
                  </Link>
                  <Link to="/use-cases/client-management" className="px-4 py-2 text-sm font-medium text-neutral-600 hover:text-[#2B211A] hover:bg-neutral-50 transition-colors" data-id="element-1222">
                    Client Management
                  </Link>
                  <Link to="/use-cases/growth-marketing" className="px-4 py-2 text-sm font-medium text-neutral-600 hover:text-[#2B211A] hover:bg-neutral-50 transition-colors" data-id="element-1223">
                    Growth & Marketing
                  </Link>
                  <Link to="/enterprise" className="px-4 py-2 text-sm font-medium text-neutral-600 hover:text-[#2B211A] hover:bg-neutral-50 transition-colors" data-id="element-1224">
                    Enterprise
                  </Link>
                </div>
              </div>
            </div>
            <Link to="/#integrations" className={navLinkClass} data-id="element-1225">
              Integrations
            </Link>
            <Link to="/security" className={navLinkClass} data-id="element-1226">
              Security
            </Link>
            <div className="relative group" data-id="element-1227">
              <button className={`flex items-center gap-1 ${navLinkClass}`} data-id="element-1228">
                Resources{' '}
                <ChevronDown size={14} className="opacity-50 group-hover:rotate-180 transition-transform" data-id="element-1229" />
              </button>
              <div className="absolute top-full left-0 pt-4 opacity-0 translate-y-2 pointer-events-none group-hover:opacity-100 group-hover:translate-y-0 group-hover:pointer-events-auto transition-all duration-200" data-id="element-1230">
                <div className="bg-white border border-neutral-200 shadow-xl rounded-xl py-2 w-48 flex flex-col" data-id="element-1231">
                  <Link to="/blog" className="px-4 py-2 text-sm font-medium text-neutral-600 hover:text-[#2B211A] hover:bg-neutral-50 transition-colors" data-id="element-1232">
                    Blog
                  </Link>
                  <Link to="/research" className="px-4 py-2 text-sm font-medium text-neutral-600 hover:text-[#2B211A] hover:bg-neutral-50 transition-colors" data-id="element-1233">
                    Research
                  </Link>
                  <Link to="/case-studies" className="px-4 py-2 text-sm font-medium text-neutral-600 hover:text-[#2B211A] hover:bg-neutral-50 transition-colors" data-id="element-1234">
                    Case Studies
                  </Link>
                  <Link to="/changelog" className="px-4 py-2 text-sm font-medium text-neutral-600 hover:text-[#2B211A] hover:bg-neutral-50 transition-colors" data-id="element-1235">
                    Changelog
                  </Link>
                </div>
              </div>
            </div>
            <div className="relative group" data-id="element-1236">
              <button className={`flex items-center gap-1 ${navLinkClass}`} data-id="element-1237">
                Compare{' '}
                <ChevronDown size={14} className="opacity-50 group-hover:rotate-180 transition-transform" data-id="element-1238" />
              </button>
              <div className="absolute top-full left-0 pt-4 opacity-0 translate-y-2 pointer-events-none group-hover:opacity-100 group-hover:translate-y-0 group-hover:pointer-events-auto transition-all duration-200" data-id="element-1239">
                <div className="bg-white border border-neutral-200 shadow-xl rounded-xl py-2 w-48 flex flex-col" data-id="element-1240">
                  <Link to="/compare/chatgpt" className="px-4 py-2 text-sm font-medium text-neutral-600 hover:text-[#2B211A] hover:bg-neutral-50 transition-colors" data-id="element-1241">
                    vs ChatGPT
                  </Link>
                  <Link to="#" className="px-4 py-2 text-sm font-medium text-neutral-600 hover:text-[#2B211A] hover:bg-neutral-50 transition-colors" data-id="element-1242">
                    vs OpenClaw
                  </Link>
                  <Link to="#" className="px-4 py-2 text-sm font-medium text-neutral-600 hover:text-[#2B211A] hover:bg-neutral-50 transition-colors" data-id="element-1243">
                    vs Claude in Slack
                  </Link>
                  <Link to="#" className="px-4 py-2 text-sm font-medium text-neutral-600 hover:text-[#2B211A] hover:bg-neutral-50 transition-colors" data-id="element-1244">
                    vs Tasklet
                  </Link>
                </div>
              </div>
            </div>
          </nav>

          <div className="flex items-center gap-3 md:gap-4" data-id="element-1245">
            {atHeroTop ?
          // Hero already shows the primary "Try Giga for free" CTA — keep a
          // quiet white Sign in button here until the user scrolls past it.
          <Link to="/login" className="hidden md:block bg-white hover:bg-neutral-50 text-[#2B211A] border border-neutral-200 px-5 py-2.5 rounded-full text-sm font-bold transition-colors shadow-sm" data-id="element-1246">
                Sign in
              </Link> : <>
                <Link to="/login" className={`hidden md:block ${navLinkClass}`} data-id="element-1247">
                  Log in
                </Link>
                <Link to="/signup" className="hidden md:block bg-[#EA580C] hover:bg-[#C2410C] text-white px-5 py-2.5 rounded-full text-sm font-bold transition-colors shadow-sm" data-id="element-1248">
                  Try Giga for free
                </Link>
              </>}

            {/* Mobile control: only the hamburger is in the header. The Try For Free button floats at the bottom when scrolling down. */}
            <div className="md:hidden flex items-center gap-2" data-id="element-1249">
              <button className={`p-2 -mr-2 transition-colors ${onDark ? 'text-white' : 'text-[#2B211A]'}`} onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Toggle mobile menu" aria-expanded={mobileMenuOpen} data-id="element-1250">
                {mobileMenuOpen ? <X size={24} data-id="element-1251" /> : <Menu size={24} data-id="element-1252" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu Dropdown - Compact */}
        {mobileMenuOpen && <div className="md:hidden absolute top-full left-0 w-full bg-white border-b border-neutral-200 shadow-lg pb-3 px-5 flex flex-col gap-1.5 z-50 max-h-[80vh] overflow-y-auto" data-id="element-1253">
            <div className="sticky top-0 -mx-5 px-5 pt-3 pb-3 bg-white border-b border-neutral-100 flex gap-3 z-10" data-id="element-1254">
              <Link to="/login" className="flex-1 text-center bg-white text-[#2B211A] border border-neutral-200 px-4 py-2.5 rounded-lg text-sm font-bold shadow-sm" data-id="element-1255">
                Log in
              </Link>
              <Link to="/signup" className="flex-1 text-center bg-[#EA580C] text-white px-4 py-2.5 rounded-lg text-sm font-bold shadow-sm" data-id="element-1256">
                Try Giga for free
              </Link>
            </div>
            <Link to="/pricing" className="text-base font-medium text-[#2B211A] py-1.5 border-b border-neutral-100 mt-2" data-id="element-1257">
              Pricing
            </Link>
            <div className="border-b border-neutral-100" data-id="element-1258">
              <button onClick={() => toggleSection('use-cases')} aria-expanded={openSection === 'use-cases'} className="w-full flex items-center justify-between text-base font-medium text-[#2B211A] py-1.5" data-id="element-1259">
                Use Cases
                <ChevronDown size={18} className={`text-neutral-400 transition-transform ${openSection === 'use-cases' ? 'rotate-180' : ''}`} data-id="element-1260" />
              </button>
              <AnimatePresence initial={false} data-id="element-1261">
                {openSection === 'use-cases' && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.2,
              ease: 'easeOut'
            }} className="overflow-hidden" data-id="element-1262">
                    <div className="flex flex-col gap-0.5 pb-1.5" data-id="element-1263">
                      <Link to="/use-cases/founders-execs" className="text-sm font-medium text-[#2B211A] pl-2 py-1" data-id="element-1264">
                        Founders & Execs
                      </Link>
                      <Link to="/use-cases/client-management" className="text-sm font-medium text-[#2B211A] pl-2 py-1" data-id="element-1265">
                        Client Management
                      </Link>
                      <Link to="/use-cases/growth-marketing" className="text-sm font-medium text-[#2B211A] pl-2 py-1" data-id="element-1266">
                        Growth & Marketing
                      </Link>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>
            <Link to="/#integrations" className="text-base font-medium text-[#2B211A] py-1.5 border-b border-neutral-100" data-id="element-1267">
              Integrations
            </Link>
            <Link to="/security" className="text-base font-medium text-[#2B211A] py-1.5 border-b border-neutral-100" data-id="element-1268">
              Security
            </Link>
            <Link to="/enterprise" className="text-base font-medium text-[#2B211A] py-1.5 border-b border-neutral-100" data-id="element-1269">
              Enterprise
            </Link>
            <div className="border-b border-neutral-100" data-id="element-1270">
              <button onClick={() => toggleSection('resources')} aria-expanded={openSection === 'resources'} className="w-full flex items-center justify-between text-base font-medium text-[#2B211A] py-1.5" data-id="element-1271">
                Resources
                <ChevronDown size={18} className={`text-neutral-400 transition-transform ${openSection === 'resources' ? 'rotate-180' : ''}`} data-id="element-1272" />
              </button>
              <AnimatePresence initial={false} data-id="element-1273">
                {openSection === 'resources' && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.2,
              ease: 'easeOut'
            }} className="overflow-hidden" data-id="element-1274">
                    <div className="flex flex-col gap-0.5 pb-1.5" data-id="element-1275">
                      <Link to="/blog" className="text-sm font-medium text-[#2B211A] pl-2 py-1" data-id="element-1276">
                        Blog
                      </Link>
                      <Link to="/research" className="text-sm font-medium text-[#2B211A] pl-2 py-1" data-id="element-1277">
                        Research
                      </Link>
                      <Link to="/case-studies" className="text-sm font-medium text-[#2B211A] pl-2 py-1" data-id="element-1278">
                        Case Studies
                      </Link>
                      <Link to="/changelog" className="text-sm font-medium text-[#2B211A] pl-2 py-1" data-id="element-1279">
                        Changelog
                      </Link>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>
            <div className="border-b border-neutral-100" data-id="element-1280">
              <button onClick={() => toggleSection('compare')} aria-expanded={openSection === 'compare'} className="w-full flex items-center justify-between text-base font-medium text-[#2B211A] py-1.5" data-id="element-1281">
                Compare
                <ChevronDown size={18} className={`text-neutral-400 transition-transform ${openSection === 'compare' ? 'rotate-180' : ''}`} data-id="element-1282" />
              </button>
              <AnimatePresence initial={false} data-id="element-1283">
                {openSection === 'compare' && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.2,
              ease: 'easeOut'
            }} className="overflow-hidden" data-id="element-1284">
                    <div className="flex flex-col gap-0.5 pb-1.5" data-id="element-1285">
                      <Link to="/compare/chatgpt" className="text-sm font-medium text-[#2B211A] pl-2 py-1" data-id="element-1286">
                        vs ChatGPT
                      </Link>
                      <Link to="#" className="text-sm font-medium text-[#2B211A] pl-2 py-1" data-id="element-1287">
                        vs OpenClaw
                      </Link>
                      <Link to="#" className="text-sm font-medium text-[#2B211A] pl-2 py-1" data-id="element-1288">
                        vs Claude in Slack
                      </Link>
                      <Link to="#" className="text-sm font-medium text-[#2B211A] pl-2 py-1" data-id="element-1289">
                        vs Tasklet
                      </Link>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>
          </div>}
      </header>

      {/* Mobile Floating CTA - visible only when scrolling down */}
      <AnimatePresence data-id="element-1290">
        {isScrolled && scrollDirection === 'down' && <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 20
      }} transition={{
        duration: 0.2
      }} className="md:hidden fixed bottom-6 left-0 right-0 z-40 flex justify-center px-6 pointer-events-none" data-id="element-1291">
            <Link to="/signup" className="bg-[#EA580C] hover:bg-[#C2410C] text-white px-8 py-3.5 rounded-full text-base font-bold shadow-xl pointer-events-auto" data-id="element-1292">
              Try Giga for free
            </Link>
          </motion.div>}
      </AnimatePresence>

      <main className="flex-1" data-id="element-1293">
        <Outlet data-id="element-1294" />
      </main>

      <footer className="relative overflow-hidden bg-[#EA580C] text-sm text-white/80 pt-20 pb-40 md:pb-56" data-id="element-1295">
        {/* Subtle grid texture */}
        <div className="dot-matrix opacity-20" aria-hidden="true" data-id="element-1296" />

        {/* Oversized ghosted brand wordmark — sits in the lower band of the
         footer (below the sitemap), gently bleeding off the bottom edge. */}
        <img src="https://cdn.magicpatterns.com/uploads/fUuKba8qU1ywiSyZ4Pf9Xu/giga4x.png" alt="" aria-hidden="true" className="pointer-events-none select-none absolute inset-x-0 -bottom-[2vw] mx-auto w-[80%] max-w-none object-contain opacity-[0.12] [filter:brightness(0)_invert(1)]" data-id="element-1297" />

        <div className="relative z-10 max-w-[1280px] mx-auto px-6 md:px-10" data-id="element-1298">
          {/* Sitemap */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-10 md:gap-8 pb-16 border-b border-white/20" data-id="element-1299">
            {/* Brand column */}
            <div className="col-span-2 lg:col-span-1" data-id="element-1300">
              <img src="https://cdn.magicpatterns.com/uploads/fUuKba8qU1ywiSyZ4Pf9Xu/giga4x.png" alt="Giga" className="h-7 object-contain mb-4" data-id="element-1301" />
              <p className="text-[#2B211A]/60 leading-relaxed max-w-xs" data-id="element-1302">
                The AI coworker that takes action across your work and your
                life.
              </p>
            </div>

            {[{
            title: 'Product',
            links: [{
              label: 'Pricing',
              to: '/pricing'
            }, {
              label: 'Security',
              to: '/security'
            }, {
              label: 'Enterprise',
              to: '/enterprise'
            }, {
              label: 'Giga vs ChatGPT',
              to: '/compare/chatgpt'
            }]
          }, {
            title: 'Use cases',
            links: [{
              label: 'Founders & Execs',
              to: '/use-cases/founders-execs'
            }, {
              label: 'Client Management',
              to: '/use-cases/client-management'
            }, {
              label: 'Growth & Marketing',
              to: '/use-cases/growth-marketing'
            }]
          }, {
            title: 'Resources',
            links: [{
              label: 'Blog',
              to: '/blog'
            }, {
              label: 'Research',
              to: '/research'
            }, {
              label: 'Case Studies',
              to: '/case-studies'
            }, {
              label: 'Changelog',
              to: '/changelog'
            }]
          }, {
            title: 'Company',
            links: [{
              label: 'About',
              to: '/about'
            }, {
              label: 'Careers',
              to: '/careers'
            }, {
              label: 'Contact',
              to: '/contact'
            }]
          }, {
            title: 'Legal',
            links: [{
              label: 'Privacy',
              to: '/privacy'
            }, {
              label: 'Terms',
              to: '/terms'
            }]
          }].map(col => <div key={col.title} data-id="element-1303">
                <h3 className="text-[11px] font-bold tracking-[1.5px] uppercase text-white/60 mb-4" data-id="element-1304">
                  {col.title}
                </h3>
                <ul className="space-y-3" data-id="element-1305">
                  {col.links.map(link => <li key={link.label} data-id="element-1306">
                      <Link to={link.to} className="text-white/80 hover:text-white transition-colors" data-id="element-1307">
                        {link.label}
                      </Link>
                    </li>)}
                </ul>
              </div>)}
          </div>

          {/* Bottom bar */}
          <div className="pt-8 flex flex-col md:flex-row justify-between items-center gap-4" data-id="element-1308">
            <div className="text-white/60" data-id="element-1309">
              © {new Date().getFullYear()} Giga Inc. All rights reserved.
            </div>
            <div className="flex gap-6" data-id="element-1310">
              {['Twitter', 'GitHub', 'LinkedIn', 'Discord'].map(social => <a key={social} href="#" className="text-white/80 hover:text-white transition-colors" data-id="element-1311">
                  {social}
                </a>)}
            </div>
          </div>
        </div>
      </footer>
    </div>;
}